import { useCallback, useRef, useState } from 'react'
import { newKey } from '../api/client'

/**
 * useOperation owns the Idempotency-Key behind a single user intent.
 *
 * The key is minted once and reused for every retry of that intent — it is only
 * rotated after the server confirms success, or when the operator changes the
 * inputs (newIntent). That is what makes "刷新页面 / 断网 / 再点一次" safe: a
 * replay returns the first committed result instead of selling twice.
 */
export function useOperation<T = unknown>(prefix = 'op') {
  const keyRef = useRef(newKey(prefix))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [result, setResult] = useState<T | null>(null)

  const run = useCallback(async (fn: (key: string) => Promise<T>): Promise<T | null> => {
    setBusy(true)
    setError(null)
    try {
      const out = await fn(keyRef.current)
      setResult(out)
      keyRef.current = newKey(prefix)
      return out
    } catch (e) {
      setError(e)
      return null
    } finally {
      setBusy(false)
    }
  }, [prefix])

  const newIntent = useCallback(() => {
    keyRef.current = newKey(prefix)
    setError(null)
    setResult(null)
  }, [prefix])

  return { busy, error, result, run, newIntent, setResult }
}
